import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Typography, Box } from "@mui/material";
import {
  Edit,
  Delete,
  DirectionsCar,
  PersonAddAlt1,
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";

// Redux
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";

// Services
import VehicleApi from "Services/Vehicle";

// Modals
import VehicleCreateModal from "Pages/Modals/Vehicle/Create";
import VehicleUpdateModal from "Pages/Modals/Vehicle/Update";
import VehicleDeleteModal from "Pages/Modals/Vehicle/Delete";
import AssignDriverModal from "Pages/Modals/Vehicle/AssignDriver";
import { apiUrl } from "../../Constants";

const Vehicle = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  

  const [row, setRow] = useState([]);
  const [bulkrow, setBulkrow] = useState([]);
  const [vehicleId, setVehicleId] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");

  // ---- Modals ----
  const [addVehicleModal, setAddVehicleModal] = useState(false);
  const handleOpenAddModal = () => setAddVehicleModal(true);
  const handleCloseAddModal = () => {
    setAddVehicleModal(false);
    getVehicles();
  };

  const [updateVehicleModal, setUpdateVehicleModal] = useState(false);
  const handleOpenUpdateModal = (id) => {
    setVehicleId(id);
    setUpdateVehicleModal(true);
  };
  const handleCloseUpdateModal = () => {
    setUpdateVehicleModal(false);
    getVehicles();
  };

  const [deleteVehicleModal, setDeleteVehicleModal] = useState(false);
  const handleOpenDeleteModal = (id) => {
    setVehicleId(id);
    setDeleteVehicleModal(true);
  };
  const handleCloseDeleteModal = () => {
    setDeleteVehicleModal(false);
    getVehicles();
  };

  const [assignDriverModal, setAssignDriverModal] = useState(false);
  const [assignVehicleRow, setAssignVehicleRow] = useState(null);
  const handleOpenAssignModal = (row) => {
    setAssignVehicleRow(row);
    setAssignDriverModal(true);
  };
  const handleCloseAssignModal = () => {
    setAssignDriverModal(false);
    getVehicles();
  };

  // ---- Fetch Vehicles ----
  const getVehicles = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    VehicleApi.getVehicleList({ page, limit: pageSize, search: searchText }).then(
      (res) => {
        dispatch(hideLoader());
        if (res?.data?.status === true) {
          setTotalPages(res?.data?.data?.totalRecords || 1);
          setRow(res?.data?.data?.data || []);
        } else {
          setRow([]);
        }
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch vehicles",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [page, pageSize, searchText]);

  const getAllVehicles = useCallback(() => {
    VehicleApi.getVehicleList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => {
        if (res?.data?.status === true) {
          setBulkrow(res?.data?.data?.data || []);
        }
      },
      () => {}
    );
  }, []);

  useEffect(() => {
    getVehicles();
    getAllVehicles();
  }, [getVehicles]);

  const calculateSno = (pageIndex, pageSize, index) =>
    (pageIndex - 1) * pageSize + index + 1;

  const dataRows = (row || []).map((data, index) => ({
    sno: calculateSno(page, pageSize, index),
    vehicle_name: data?.vehicle_name || "--",
    vehicle_no: data?.vehicle_no || "--",
    seat: data?.seat ?? "--",
    service_type: data?.service_type_id?.serviceName || data?.service_type_id?.name || "--",
    vehicle_manufacturer: data?.vehicle_manufacturer || "--",
    manufacturing_year: data?.manufacturing_year || "--",
    vehicle_brand: data?.vehicle_brand || "--",
    vehicle_color: data?.vehicle_color || "--",
    insurance_no: data?.insurance_no || "--",
    insurance_exp: data?.insurance_exp
      ? new Date(data.insurance_exp).toLocaleDateString()
      : "--",
    assignedDriver: data?.assignedDriver
      ? `${data.assignedDriver.firstName} ${data.assignedDriver.lastName}`
      : "--",
    status: data?.status,
    vehicle_image: `${apiUrl}${data.vehicle_image_local}` || "",
    raw: data,
  }));

  // Bulk rows for export
  const bulkDataRows = _.orderBy(bulkrow || [], ["createdAt"], ["asc"]).map(
    (data, index) => ({
      sno: index + 1,
      vehicle_name: data?.vehicle_name || "--",
      vehicle_no: data?.vehicle_no || "--",
      seat: data?.seat ?? "--",
      service_type: data?.service_type_id?.serviceName || data?.service_type_id?.name || "--",
      vehicle_manufacturer: data?.vehicle_manufacturer || "--",
      manufacturing_year: data?.manufacturing_year || "--",
      vehicle_brand: data?.vehicle_brand || "--",
      vehicle_color: data?.vehicle_color || "--",
      insurance_no: data?.insurance_no || "--",
      insurance_exp: data?.insurance_exp
        ? new Date(data.insurance_exp).toLocaleDateString()
        : "--",
      assignedDriver: data?.assignedDriver
        ? `${data.assignedDriver.firstName} ${data.assignedDriver.lastName}`
        : "--",
      status: data?.status === 1 ? "Active" : "Inactive",
    })
  );

  const formattedExportData = dataRows.map((r) => ({
    "S.No": r.sno,
    "Car Model": r.vehicle_name,
    "License Plate": r.vehicle_no,
    "Seat Capacity": r.seat,
    "Service Type": r.service_type,
    Manufacturer: r.vehicle_manufacturer,
    "Mfg Year": r.manufacturing_year,
    Brand: r.vehicle_brand,
    Color: r.vehicle_color,
    "Insurance No": r.insurance_no,
    "Insurance Exp": r.insurance_exp,
    "Assigned Driver": r.assignedDriver,
    Status: r.status === 1 ? "Active" : "Inactive",
  }));

  const bulkFormattedData = bulkDataRows.map((r) => ({
    "S.No": r.sno,
    "Car Model": r.vehicle_name,
    "License Plate": r.vehicle_no,
    "Seat Capacity": r.seat,
    "Service Type": r.service_type,
    Manufacturer: r.vehicle_manufacturer,
    "Mfg Year": r.manufacturing_year,
    Brand: r.vehicle_brand,
    Color: r.vehicle_color,
    "Insurance No": r.insurance_no,
    "Insurance Exp": r.insurance_exp,
    "Assigned Driver": r.assignedDriver,
    Status: r.status,
  }));

  // ---- Export Handlers ----
  const handleCopy = () => {
    const colWidths = [5, 20, 15, 6, 15, 18, 8, 15, 10, 12, 12, 20, 8];
    const pad = (text, width) =>
      (text ?? "").toString().padEnd(width, " ");
    const headers = [
      "S.NO", "Car Model", "Plate No", "Seat", "Service", "Manufacturer",
      "Year", "Brand", "Color", "Ins. No", "Ins. Exp", "Driver", "Status",
    ]
      .map((h, i) => pad(h, colWidths[i]))
      .join("");
    const rows = dataRows
      .map((r) =>
        [
          r.sno, r.vehicle_name, r.vehicle_no, r.seat, r.service_type,
          r.vehicle_manufacturer, r.manufacturing_year, r.vehicle_brand,
          r.vehicle_color, r.insurance_no, r.insurance_exp, r.assignedDriver,
          r.status === 1 ? "Active" : "Inactive",
        ]
          .map((v, i) => pad(v, colWidths[i]))
          .join("")
      )
      .join("\n");
    navigator.clipboard
      .writeText([headers, rows].join("\n"))
      .then(() => alert("Table copied to clipboard!"));
  };

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(formattedExportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
    XLSX.writeFile(wb, "VehicleList.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formattedExportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "VehicleList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkFormattedData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "VehicleList_ALL.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const tableData = dataRows.map((r) => [
      r.sno, r.vehicle_name, r.vehicle_no, r.seat, r.service_type,
      r.vehicle_manufacturer, r.manufacturing_year, r.vehicle_brand,
      r.vehicle_color, r.assignedDriver, r.status === 1 ? "Active" : "Inactive",
    ]);
    doc.autoTable({
      head: [[
        "S.No", "Car Model", "Plate No", "Seat", "Service", "Manufacturer",
        "Year", "Brand", "Color", "Driver", "Status",
      ]],
      body: tableData,
      styles: { fontSize: 8 },
    });
    doc.save("VehicleList.pdf");
  };

  const handlePageChange = (newPage) => {
    const p = parseInt(newPage) + 1;
    if (p >= 1) setPage(p);
  };

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  // ---- Columns ----
  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.lightText}>
          <Typography variant="body2">{params.row.sno}</Typography>
        </div>
      ),
    },
    {
      field: "vehicle_image",
      headerName: "Image",
      minWidth: 80,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.vehicle_image ? (
          <img
            src={params.row.vehicle_image}
            alt="vehicle"
            style={{
              width: 50,
              height: 40,
              objectFit: "cover",
              borderRadius: 6,
              border: "1px solid #ddd",
            }}
          />
        ) : (
          <DirectionsCar style={{ fontSize: 32, color: "#bbb" }} />
        ),
    },
    {
      field: "vehicle_name",
      headerName: "Car Model",
      minWidth: 130,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.vehicle_name}</Typography>
      ),
    },
    {
      field: "vehicle_no",
      headerName: "License Plate",
      minWidth: 140,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {params.row.vehicle_no}
        </Typography>
      ),
    },
    {
      field: "assignedDriver",
      headerName: "Assigned Driver",
      minWidth: 160,
      renderCell: (params) => (
        <Button
          variant={params.row.assignedDriver === "--" ? "outlined" : "text"}
          color="primary"
          size="small"
          onClick={() => handleOpenAssignModal(params.row)}
          style={{ textTransform: "none", fontWeight: 600 }}
          startIcon={params.row.assignedDriver === "--" ? <PersonAddAlt1 fontSize="small" /> : null}
        >
          {params.row.assignedDriver === "--" ? "Assign Driver" : params.row.assignedDriver}
        </Button>
      ),
    },
    {
      field: "vehicle_brand",
      headerName: "Brand",
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.vehicle_brand}</Typography>
      ),
    },
    {
      field: "vehicle_manufacturer",
      headerName: "Manufacturer",
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.vehicle_manufacturer}</Typography>
      ),
    },
    {
      field: "manufacturing_year",
      headerName: "Year",
      minWidth: 80,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.manufacturing_year}</Typography>
      ),
    },
    {
      field: "seat",
      headerName: "Seats",
      minWidth: 70,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.seat}</Typography>
      ),
    },
    {
      field: "vehicle_color",
      headerName: "Color",
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.vehicle_color}</Typography>
      ),
    },
    {
      field: "service_type",
      headerName: "Service Type",
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.service_type}</Typography>
      ),
    },
    {
      field: "insurance_no",
      headerName: "Insurance No",
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.insurance_no}</Typography>
      ),
    },
    {
      field: "insurance_exp",
      headerName: "Insurance Exp",
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.insurance_exp}</Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <span
          className={
            params.row.status === 1
              ? classes.statusActive
              : classes.statusInactive
          }
        >
          {params.row.status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            style={{ minWidth: 36, padding: "4px 8px" }}
            onClick={() => handleOpenUpdateModal(params.row.raw._id)}
          >
            <Edit fontSize="small" />
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            style={{
              minWidth: 36,
              padding: "4px 8px",
              backgroundColor: "#e5393526",
            }}
            onClick={() => handleOpenDeleteModal(params.row.raw._id)}
          >
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Grid container className={classes.root}>
        {/* Export Buttons */}
        <Grid item xs={1} md={1} className={classes.funBtn}>
          <Button variant="contained" size="small" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>
            COPY
          </Button>
        </Grid>
        <Grid item xs={1} md={1} className={classes.funBtn}>
          <Button
            variant="contained"
            size="small"
            startIcon={<TableChartOutlined />}
            onClick={handleDownloadExcel}
          >
            EXCEL
          </Button>
        </Grid>
        <Grid item xs={1} md={1} className={classes.funBtn}>
          <Button
            variant="contained"
            size="small"
            startIcon={<DescriptionOutlined />}
            onClick={handleDownloadCSV}
          >
            CSV
          </Button>
        </Grid>
        <Grid item xs={1} md={1} className={classes.funBtn}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PictureAsPdfOutlined />}
            onClick={handleDownloadPDF}
          >
            PDF
          </Button>
        </Grid>
        <Grid item xs={2} md={2} className={classes.funBtn}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownloadOutlined />}
            onClick={handleDownloadAll}
          >
            Download ALL
          </Button>
        </Grid>

        {/* Add Vehicle Button */}
        <Grid item xs={6} md={6} className={classes.addBtn}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAddModal}
            startIcon={<DirectionsCar />}
          >
            Add Vehicle
          </Button>
        </Grid>

        {/* DataGrid */}
        <Grid item xs={12}>
          <section className={classes.dataGridTable}>
            <DataGrid
              getRowId={(row) => row?.raw?._id}
              rows={dataRows}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(1);
              }}
              onPageChange={(newPage) => handlePageChange(newPage)}
              rowsPerPageOptions={[5, 10, 25, 50]}
              pagination
              autoHeight
              disableSelectionOnClick
              scrollbarSize={4}
              rowHeight={70}
              style={{
                padding: "10px",
                borderRadius: "0px",
                overflowX: "auto",
              }}
              paginationMode="server"
              rowCount={totalPages}
              filterMode="server"
              onFilterModelChange={(model) => {
                const value =
                  model?.quickFilterValues?.join(" ") || "";
                setSearchText(value);
              }}
              density="compact"
              components={{ Toolbar: CustomToolbar }}
            />
          </section>
        </Grid>
      </Grid>

      {/* Modals */}
      <VehicleCreateModal
        openDialog={addVehicleModal}
        handleClose={handleCloseAddModal}
      />
      <VehicleUpdateModal
        openDialog={updateVehicleModal}
        handleClose={handleCloseUpdateModal}
        vehicleId={vehicleId}
      />
      <VehicleDeleteModal
        openDialog={deleteVehicleModal}
        handleClose={handleCloseDeleteModal}
        vehicleId={vehicleId}
      />
      <AssignDriverModal
        openDialog={assignDriverModal}
        handleClose={handleCloseAssignModal}
        vehicleRow={assignVehicleRow}
      />
    </>
  );
};

export default Vehicle;
